import { useEffect, useMemo, useState } from 'react'
import { marked } from 'marked'
import type { Locale, Translations } from '../../i18n/translations'

const TERMS_PATH = '/legal/terms-and-conditions.md'
const PT_HEADER_ACCENTED = '# Termos e Condicoes de Uso'
const PT_HEADER_ORIGINAL = '# Termos e Condições de Uso'
const ES_HEADER = '# Terminos y Condiciones de Uso'
const ES_HEADER_ORIGINAL = '# Términos y Condiciones de Uso'

interface TermsAndConditionsPageProps {
  locale: Locale
  t: Translations
}

interface ParsedTermsSections {
  en: string
  'pt-BR': string
}

function findHeaderIndex(source: string, header: string): number {
  return source.indexOf(header)
}

export function extractLocalizedTerms(markdown: string): ParsedTermsSections {
  const ptIndex =
    findHeaderIndex(markdown, PT_HEADER_ORIGINAL) >= 0
      ? findHeaderIndex(markdown, PT_HEADER_ORIGINAL)
      : findHeaderIndex(markdown, PT_HEADER_ACCENTED)
  const esIndex =
    findHeaderIndex(markdown, ES_HEADER_ORIGINAL) >= 0
      ? findHeaderIndex(markdown, ES_HEADER_ORIGINAL)
      : findHeaderIndex(markdown, ES_HEADER)

  if (ptIndex < 0 || esIndex < 0 || ptIndex >= esIndex) {
    return {
      en: markdown,
      'pt-BR': markdown
    }
  }

  return {
    en: markdown.slice(0, ptIndex).trim(),
    'pt-BR': markdown.slice(ptIndex, esIndex).trim()
  }
}

export function TermsAndConditionsPage({ locale, t }: TermsAndConditionsPageProps): JSX.Element {
  const [markdown, setMarkdown] = useState<string>('')
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let disposed = false

    async function loadTerms(): Promise<void> {
      setLoadError(null)
      try {
        const response = await fetch(TERMS_PATH)
        if (!response.ok) {
          throw new Error(`Failed to load terms: ${response.status}`)
        }
        const text = await response.text()
        if (!disposed) {
          setMarkdown(text)
        }
      } catch {
        if (!disposed) {
          setLoadError(t.legalLoadError)
        }
      }
    }

    void loadTerms()

    return () => {
      disposed = true
    }
  }, [t.legalLoadError])

  const termsHtml = useMemo(() => {
    if (!markdown) return ''
    const sections = extractLocalizedTerms(markdown)
    const selected = sections[locale]
    return marked.parse(selected, { gfm: true, breaks: true }) as string
  }, [markdown, locale])

  if (loadError) {
    return (
      <section className="marketing-panel legal-panel" aria-label={t.publicTerms}>
        <p className="marketing-kicker">{t.publicTerms}</p>
        <h2 className="marketing-title">{t.legalTermsTitle}</h2>
        <p className="marketing-copy">{loadError}</p>
      </section>
    )
  }

  if (!termsHtml) {
    return (
      <section className="marketing-panel legal-panel" aria-label={t.publicTerms}>
        <p className="marketing-kicker">{t.publicTerms}</p>
        <h2 className="marketing-title">{t.legalTermsTitle}</h2>
        <p className="marketing-copy">{t.loading}</p>
      </section>
    )
  }

  return (
    <section className="marketing-panel legal-panel" aria-label={t.publicTerms}>
      <p className="marketing-kicker">{t.publicTerms}</p>
      <h2 className="marketing-title">{t.legalTermsTitle}</h2>
      <div className="legal-markdown" dangerouslySetInnerHTML={{ __html: termsHtml }} />
    </section>
  )
}
